import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";

/**
 * AB#38825: lets a centred Quick Entry dialog be dragged by its header.
 *
 * The specialist fills the create-new patient/requestor form FROM the request document,
 * and the dialog opened dead-centre over it with no way to move it - so the details being
 * transcribed were behind the form doing the transcribing. This hook is the smallest thing
 * that unblocks that: a header drag handle and an (x, y) offset the dialog applies to its
 * own transform.
 *
 * WHY AN OFFSET AND NOT left/top. The dialog is centred by CSS the hook must not fight:
 * `top: 50%; left: 50%; transform: translate(-50%, -50%)` (CreateEntityFormModal.scss,
 * MatchGridModal.scss). Switching to absolute left/top on first drag would recompute the
 * centring at a different moment than the stylesheet does and make the dialog JUMP by half
 * its own size on the first mousedown. Composing into the SAME transform -
 * `translate(calc(-50% + Xpx), calc(-50% + Ypx))` - leaves the centring exactly where the
 * stylesheet put it and keeps `max-width: 92vw` / `max-height: 82vh` and the body's
 * `overflow-y: auto` scroll region working untouched.
 *
 * MOUSE EVENTS, NOT POINTER EVENTS. The jest environment is jsdom 14.1.0 (react-scripts 3.4.1
 * -> jest-environment-jsdom-fourteen), which does not implement PointerEvent at all -
 * `typeof window.PointerEvent === "undefined"` - so a pointer-event implementation could not
 * be driven by a single test in this repo. Mouse events are fully supported by jsdom and by
 * every browser the app targets.
 *
 * Prior art, and where this deliberately differs: DragTableWrapper.tsx (AB#35147, "Requestor
 * Search modal is not draggable") is the repo's existing hand-rolled drag, and the
 * document-level mousemove/mouseup discipline below is taken straight from it. What is NOT
 * taken from it is its positioning: it snaps the popup's centre to the cursor on mousedown
 * (`left: mousePositionX - width/2`), so the dialog TELEPORTS out from under the pointer on
 * grab, and it escapes the centring CSS by switching to pixel top/left. Accumulating a
 * delta into the existing transform needs neither: the dialog stays exactly where it was
 * when grabbed, and the stylesheet's centring is never fought.
 *
 * The offset is component state and the dialog remounts on each open (`createOpen ? <M/> : null`),
 * so every open starts centred again. That is deliberate: there is no stuck-off-screen state to
 * recover from, and no persistence to invalidate.
 */

/** Pixels of the dialog that must stay on screen when it is dragged left, right or down. */
export const MIN_VISIBLE_PX = 48;

export interface ModalOffset {
    readonly x: number;
    readonly y: number;
}

/** A rect shape - `DOMRect` in the browser, a plain literal in tests. */
export interface MeasuredRect {
    readonly width: number;
    readonly height: number;
}

export interface Viewport {
    readonly width: number;
    readonly height: number;
}

/**
 * Confines a proposed drag offset so at least MIN_VISIBLE_PX of the dialog stays on every
 * edge. Without this the dialog can be thrown past the viewport and neither dragged back nor
 * read - and it holds a part-typed form, so losing it costs the specialist their entry.
 *
 * Pure and exported so the geometry is unit-testable against synthetic rects: jsdom reports a
 * zero-sized `getBoundingClientRect()` for every element, so a clamp verified only through a
 * rendered component would be verified against width 0 and prove nothing.
 *
 * A DEGENERATE rect (either dimension 0) returns the offset unchanged. That covers both jsdom
 * and the real pre-layout moment; clamping a box we cannot measure would invent a limit from
 * a size we do not know.
 *
 * The dialog is CENTRED, so its own box spans `half` either side of the viewport centre: an
 * offset of `x` puts its left edge at `viewport.width / 2 - halfW + x`. Solving for "left edge
 * at most `viewport.width - MIN_VISIBLE_PX`" and "right edge at least `MIN_VISIBLE_PX`" gives
 * the bounds below.
 */
export function clampModalOffset(offset: ModalOffset, rect: MeasuredRect, viewport: Viewport): ModalOffset {
    if (rect.width <= 0 || rect.height <= 0) {
        return offset;
    }
    const halfW = rect.width / 2;
    const halfH = rect.height / 2;
    const centreX = viewport.width / 2;
    const centreY = viewport.height / 2;

    // left  = centreX - halfW + x  >=  MIN_VISIBLE_PX - rect.width   (keep MIN_VISIBLE on the right edge)
    // left  = centreX - halfW + x  <=  viewport.width - MIN_VISIBLE_PX (keep MIN_VISIBLE on the left edge)
    const minX = MIN_VISIBLE_PX - rect.width - centreX + halfW;
    const maxX = viewport.width - MIN_VISIBLE_PX - centreX + halfW;

    // The vertical bounds are ASYMMETRIC, and deliberately so: THE HANDLE IS THE HEADER, and the
    // header is at the top of the dialog. A symmetric "keep MIN_VISIBLE_PX of the box on every
    // edge" rule looks right and is not - it permits dragging up until only the dialog's BOTTOM
    // strip is on screen, with the header above the viewport. The dialog is then unreachable:
    // nothing left to grab, and it is holding a part-typed form. So upward travel stops with the
    // top edge at 0 (`top = centreY - halfH + y >= 0`), which keeps the whole header in view,
    // while downward travel only has to leave the usual sliver.
    const minY = halfH - centreY;
    const maxY = viewport.height - MIN_VISIBLE_PX - centreY + halfH;

    // Neither span can invert, so a plain clamp is safe and no min/max ordering dance is
    // needed: maxX - minX = viewport.width - 2 * MIN_VISIBLE_PX + rect.width, and
    // maxY - minY = viewport.height - MIN_VISIBLE_PX. Both are positive for any dialog the
    // stylesheet can produce (max-width: 92vw, max-height: 82vh keep it inside the viewport).
    return {
        x: Math.min(Math.max(offset.x, minX), maxX),
        y: Math.min(Math.max(offset.y, minY), maxY),
    };
}

/**
 * True when a mousedown landed on something that owns the click itself, so the drag must not
 * start: the header's Close button, and any control a consumer puts in its header. Without
 * this, pressing Close would begin a drag and `preventDefault()` would suppress the very click
 * that closes the dialog.
 *
 * The list covers non-native controls too - `[role="button"]`, `[tabindex]`, `[contenteditable]`,
 * `<label>` (whose click forwards to its control) and `<summary>` - because a consumer's header
 * is not required to be built from native elements, and a control that silently became
 * undraggable-over would be a confusing bug to trace back to here.
 */
const INTERACTIVE_IN_HANDLE =
    "button, a, input, select, textarea, label, summary, [role=\"button\"], [role=\"link\"], [contenteditable], [tabindex]";

/**
 * The search is BOUNDED BY THE HANDLE, and that bound is load-bearing rather than tidiness:
 * `closest()` walks all the way up, and the dialog root itself carries `tabIndex={-1}` for the
 * focus trap. An unbounded `closest("[tabindex]")` therefore matched the dialog from anywhere in
 * the header and refused every drag. Only a control INSIDE the handle owns its own click.
 */
function isInteractiveTarget(target: EventTarget | null, handle: Element): boolean {
    if (!(target instanceof Element)) {
        return false;
    }
    const hit = target.closest(INTERACTIVE_IN_HANDLE);
    return hit !== null && hit !== handle && handle.contains(hit);
}

export interface ModalDragHandle {
    /** Current offset from the CSS-centred position, in px. */
    readonly offset: ModalOffset;
    /** Spread onto the drag handle (the dialog header). */
    readonly onHandleMouseDown: (event: React.MouseEvent) => void;
    /** True while a drag is in flight - consumers use it to suppress text selection. */
    readonly dragging: boolean;
}

/**
 * @param dialogRef ref to the dialog root, measured on drag start so the clamp knows the box.
 */
export function useModalDrag(dialogRef: React.RefObject<HTMLElement>): ModalDragHandle {
    const [offset, setOffset] = useState<ModalOffset>({ x: 0, y: 0 });
    const [dragging, setDragging] = useState(false);

    // Read by the document-level listeners, which are attached once per drag and would otherwise
    // close over the offset as it stood at mousedown.
    const originRef = useRef({ pointerX: 0, pointerY: 0, offsetX: 0, offsetY: 0 });
    const offsetRef = useRef<ModalOffset>({ x: 0, y: 0 });
    /** Detaches the current drag's listeners. Non-null exactly while a drag is in flight. */
    const stopRef = useRef<(() => void) | null>(null);

    /** The dialog's box RIGHT NOW. Re-read per move rather than cached at mousedown: the dialog
     *  grows when a validation message appears under a field, and a clamp using the stale height
     *  would let the header be dragged above the viewport - the one thing the clamp exists to
     *  prevent. A zero rect (jsdom, or before first layout) makes clampModalOffset a no-op. */
    const measure = useCallback((): MeasuredRect => {
        const r = dialogRef.current?.getBoundingClientRect();
        return { width: r?.width ?? 0, height: r?.height ?? 0 };
    }, [dialogRef]);

    const applyOffset = useCallback((next: ModalOffset) => {
        const clamped = clampModalOffset(next, measure(),
            { width: window.innerWidth, height: window.innerHeight });
        offsetRef.current = clamped;
        setOffset(clamped);
    }, [measure]);

    const endDrag = useCallback(() => {
        if (stopRef.current) {
            stopRef.current();
            stopRef.current = null;
        }
        setDragging(false);
    }, []);

    const onHandleMouseDown = useCallback((event: React.MouseEvent) => {
        // Left button only: a right-click opens the context menu and a middle-click pastes on some
        // platforms - neither should silently begin a drag.
        if (event.button !== 0 || stopRef.current
            || isInteractiveTarget(event.target, event.currentTarget as Element)) {
            return;
        }
        // Stops the browser selecting the title text as the pointer sweeps across it.
        event.preventDefault();
        originRef.current = {
            pointerX: event.clientX, pointerY: event.clientY,
            offsetX: offsetRef.current.x, offsetY: offsetRef.current.y,
        };

        const onMouseMove = (move: MouseEvent) => {
            // `buttons` is a BITMASK of what is held right now, and it is the only way to notice a
            // mouseup the page never received - released over browser chrome, the OS taskbar, a
            // second monitor, or after an alt-tab. Without this the dialog silently follows the
            // cursor forever with no button held, and the next click drops it somewhere random.
            if ((move.buttons & 1) === 0) {
                endDrag();
                return;
            }
            const origin = originRef.current;
            applyOffset({
                x: origin.offsetX + (move.clientX - origin.pointerX),
                y: origin.offsetY + (move.clientY - origin.pointerY),
            });
        };
        // Only the button that STARTED the drag ends it: releasing a right-click while the left is
        // still held must not tear down a gesture the specialist is in the middle of.
        const onMouseUp = (up: MouseEvent) => { if (up.button === 0) { endDrag(); } };

        // Attached HERE, synchronously, not from an effect keyed on `dragging`. A passive effect is
        // deferred, so a mouseup landing before React flushed it would never be seen and the drag
        // would stay latched on. Listeners go on `document`, not the handle, because the pointer
        // routinely outruns the header mid-drag.
        document.addEventListener("mousemove", onMouseMove);
        document.addEventListener("mouseup", onMouseUp);
        stopRef.current = () => {
            document.removeEventListener("mousemove", onMouseMove);
            document.removeEventListener("mouseup", onMouseUp);
        };
        setDragging(true);
    }, [applyOffset, endDrag]);

    /**
     * Unmount mid-drag must not leave document-level listeners behind - the dialog is unmounted on
     * every close, so a leak here is a pair of listeners per open for the life of the session.
     *
     * useLayoutEffect, not useEffect: a PASSIVE effect's destroy function is not guaranteed to have
     * run by the time the tree is gone, and measurably had not - a listener-counting test showed
     * both listeners still attached after unmount. Layout-effect cleanup runs synchronously with
     * the commit that removes the component, which is the only timing that can be relied on for
     * tearing down something attached imperatively.
     */
    useLayoutEffect(() => () => {
        if (stopRef.current) {
            stopRef.current();
            stopRef.current = null;
        }
    }, []);

    /**
     * Re-clamp when the window changes size. The offset was valid for the viewport it was dragged
     * in; shrinking the window afterwards can leave a dialog that is now entirely off-screen, with
     * nothing to grab and a part-typed form inside. Only ever pulls the dialog back into view - an
     * offset already inside the new bounds is returned unchanged.
     */
    useEffect(() => {
        const onResize = () => {
            const current = offsetRef.current;
            if (current.x === 0 && current.y === 0) {
                return;
            }
            applyOffset(current);
        };
        window.addEventListener("resize", onResize);
        return () => window.removeEventListener("resize", onResize);
    }, [applyOffset]);

    return { offset, onHandleMouseDown, dragging };
}
